<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header Section -->
    <TenantHeader :api-data="apiData" @go-back="goBack" />

    <!-- Navigation Tabs -->
    <div class="bg-white border-b border-gray-200">
      <nav class="flex space-x-8 px-6" aria-label="Tabs">
        <a
          v-for="tab in tabs"
          :key="tab.name"
          :href="tab.href"
          :class="[
            tab.current
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm cursor-pointer'
          ]"
          @click.prevent="currentTab = tab.name"
        >
          {{ tab.name }}
        </a>
      </nav>
    </div>

    <!-- Main Content -->
    <div class="p-6">
      <!-- Header with Filter Bar on right -->
      <div class="flex justify-between items-start mb-6">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p class="text-gray-600">
            View comprehensive tenant analytics and usage statistics
          </p>
        </div>
        <!-- Filter Bar moved to right -->
        <FilterBar
          :filters="filters"
          @filter-changed="handleFilterChange"
          @date-filter-applied="handleDateFilterApplied"
          @date-filter-cleared="handleDateFilterCleared"
        />
      </div>

      <!-- Right Sidebar with Activity Log and Office Check-in -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Left section with stats cards -->
        <div class="lg:col-span-2">
          <StatsGrid
            :analytics="analytics"
            :office-checkin-status="officeCheckinStatus"
          />
        </div>

        <!-- Right sidebar -->
        <div class="space-y-6">
          <!-- Activity Log -->
          <ActivityLog
            :current-date="currentDate"
            :recent-activities="recentActivities"
            :last-updated-activity="lastUpdatedActivity"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import TenantHeader from "../components/TenantHeader.vue";
import FilterBar from "../components/FilterBar.vue";
import ActivityLog from "../components/ActivityLog.vue";
import StatsGrid from "../components/StatsGrid.vue";

export default {
  name: "StandaloneDashboard",
  components: {
    TenantHeader,
    FilterBar,
    ActivityLog,
    StatsGrid
  },
  data() {
    return {
      currentTab: "Analytics",
      tabs: [{ name: "Analytics", href: "#", current: true }],
      // Complete API response data
      apiData: null,
      // Filters
      filters: {
        user: "all",
        branch: "all"
      },
      // Date filter parameters
      dateFilters: {
        from_date: null,
        to_date: null
      },

      // Chart data
      branchData: [
        { value: 10, color: "#3B82F6", label: "Main Branch" },
        { value: 15, color: "#F59E0B", label: "Secondary Branch" }
      ],

      currentDate: "Today, March 23",

      // Analytics data for stats grid
      analytics: {},

      officeCheckinStatus: [],

      recentActivities: [],
      lastUpdatedActivity: null
    };
  },

  async created() {
    await this.fetchTenantData();
    await this.fetchActivityLog();
  },

  methods: {
    async fetchTenantData() {
      try {
        const tenantId = this.$route.params.id;
        const tenantResponse = await axios.get(
          `/tenants/${tenantId}/basic-info`
        );
        this.apiData = tenantResponse.data.data;

        await this.fetchUsageData();
      } catch (error) {
        this.apiData = null;
      }
    },

    async fetchActivityLog() {
      try {
        const tenantId = this.$route.params.id;
        const activityLogResponse = await axios.get(
          `/tenants/${tenantId}/user-activities`
        );
        this.recentActivities = activityLogResponse.data.data || [];
        this.lastUpdatedActivity =
          activityLogResponse.data.last_updated || null;
      } catch (error) {
        this.recentActivities = [];
      }
    },
    async fetchUsageData() {
      try {
        const tenantId = this.$route.params.id;
        let url = `/tenants/${tenantId}/feature-usage-data`;

        const params = new URLSearchParams();
        if (this.dateFilters.from_date) {
          params.append("from_date", this.dateFilters.from_date);
        }
        if (this.dateFilters.to_date) {
          params.append("to_date", this.dateFilters.to_date);
        }

        if (params.toString()) {
          url += "?" + params.toString();
        }

        const usageResponse = await axios.get(url);

        this.mapUsageDataToAnalytics(usageResponse.data.data);
      } catch (error) {
        this.$toast.error("Error fetching usage data");
      }
    },

    mapUsageDataToAnalytics(usageData) {
      this.analytics = {
        // Contacts
        contacts: {
          total:
            (usageData.contactsUsageData &&
              usageData.contactsUsageData.totalContacts) ||
            0,
          totalClients:
            parseInt(
              usageData.contactsUsageData &&
                usageData.contactsUsageData.totalClients
            ) || 0,
          totalProspects:
            parseInt(
              usageData.contactsUsageData &&
                usageData.contactsUsageData.totalProspects
            ) || 0,
          totalEnquiries:
            parseInt(
              usageData.contactsUsageData &&
                usageData.contactsUsageData.totalLeads
            ) || 0 // Using leads as enquiries
        },

        // Partners
        partners: {
          total:
            (usageData.partnersData && usageData.partnersData.totalPartners) ||
            0,
          imported:
            (usageData.partnersData &&
              usageData.partnersData.totalPartnerImportedFromMaster) ||
            0,
          manual:
            (usageData.partnersData &&
              usageData.partnersData.manuallyAddedPartners) ||
            0
        },

        // Applications
        applications: {
          total:
            (usageData.applicationsData &&
              usageData.applicationsData.totalApplications) ||
            0,
          discontinued:
            parseInt(
              usageData.applicationsData &&
                usageData.applicationsData.discontinuedApplications
            ) || 0,
          completed:
            parseInt(
              usageData.applicationsData &&
                usageData.applicationsData.completedApplications
            ) || 0,
          inProgress:
            parseInt(
              usageData.applicationsData &&
                usageData.applicationsData.inProgressApplications
            ) || 0
        },

        // Tasks
        tasks: {
          total: (usageData.tasksData && usageData.tasksData.totalTasks) || 0,
          inContacts:
            (usageData.tasksData &&
              usageData.tasksData.tasksByStatus &&
              usageData.tasksData.tasksByStatus.Todo) ||
            0,
          inApplications:
            (usageData.tasksData &&
              usageData.tasksData.tasksByStatus &&
              usageData.tasksData.tasksByStatus["In Progress"]) ||
            0,
          fromModule:
            (usageData.tasksData &&
              usageData.tasksData.tasksByStatus &&
              usageData.tasksData.tasksByStatus.Completed) ||
            0
        },

        // Lead Forms
        leadForms: {
          total:
            (usageData.leadFormData && usageData.leadFormData.totalLeadForms) ||
            0,
          active:
            parseInt(
              usageData.leadFormData && usageData.leadFormData.activeLeadForms
            ) || 0,
          inactive:
            parseInt(
              usageData.leadFormData &&
                usageData.leadFormData.totalInactiveLeadForms
            ) || 0
        },

        // Workflow
        workflow: {
          total:
            (usageData.workflowsData &&
              usageData.workflowsData.totalWorkflows) ||
            0,
          active:
            parseInt(
              usageData.workflowsData && usageData.workflowsData.activeWorkflows
            ) || 0,
          inactive:
            parseInt(
              usageData.workflowsData &&
                usageData.workflowsData.inactiveWorkflows
            ) || 0
        },

        // Templates
        templates: {
          sms:
            parseInt(
              usageData.templatesData && usageData.templatesData.smsTemplates
            ) || 0,
          email:
            parseInt(
              usageData.templatesData && usageData.templatesData.emailTemplates
            ) || 0,
          whatsapp:
            parseInt(
              usageData.templatesData &&
                usageData.templatesData.whatsappTemplates
            ) || 0,
          total:
            (usageData.templatesData &&
              usageData.templatesData.totalTemplates) ||
            0
        },

        // Agents
        agents: {
          total:
            (usageData.agentsData && usageData.agentsData.totalAgents) || 0,
          super:
            (usageData.agentsData && usageData.agentsData.superAgents) || 0,
          sub: (usageData.agentsData && usageData.agentsData.subAgents) || 0
        },

        // Email Campaign
        emailCampaign: {
          total:
            parseInt(
              usageData.campaignsData &&
                usageData.campaignsData.totalEmailCampaigns
            ) || 0,
          draft: 0, // Not provided in API
          completed:
            (usageData.campaignsData &&
              usageData.campaignsData.emailCampaignByStatus &&
              usageData.campaignsData.emailCampaignByStatus.completed) ||
            0,
          failed:
            (usageData.campaignsData &&
              usageData.campaignsData.emailCampaignByStatus &&
              usageData.campaignsData.emailCampaignByStatus.failed) ||
            0
        },

        // WhatsApp Campaign
        whatsappCampaign: {
          total:
            parseInt(
              usageData.campaignsData &&
                usageData.campaignsData.totalWhatsappCampaigns
            ) || 0,
          draft:
            (usageData.campaignsData &&
              usageData.campaignsData.whatsappCampaignByStatus &&
              usageData.campaignsData.whatsappCampaignByStatus.draft) ||
            0,
          completed:
            (usageData.campaignsData &&
              usageData.campaignsData.whatsappCampaignByStatus &&
              usageData.campaignsData.whatsappCampaignByStatus.completed) ||
            0,
          running:
            (usageData.campaignsData &&
              usageData.campaignsData.whatsappCampaignByStatus &&
              usageData.campaignsData.whatsappCampaignByStatus.running) ||
            0,
          revoked:
            (usageData.campaignsData &&
              usageData.campaignsData.whatsappCampaignByStatus &&
              usageData.campaignsData.whatsappCampaignByStatus.revoked) ||
            0
        },

        // Automation
        automation: {
          totalSetup:
            (usageData.automationsData &&
              usageData.automationsData.totalAutomations) ||
            0,
          active:
            (usageData.automationsData &&
              usageData.automationsData.workflowsByStatus &&
              usageData.automationsData.workflowsByStatus.Active) ||
            0,
          inactive:
            (usageData.automationsData &&
              usageData.automationsData.workflowsByStatus &&
              usageData.automationsData.workflowsByStatus.Inactive) ||
            0
        },

        // Tags
        tags: {
          total: (usageData.tagsData && usageData.tagsData.totalTags) || 0
        },

        // Appointments
        appointments: {
          total:
            (usageData.appointmentsData &&
              usageData.appointmentsData.totalAppointments) ||
            0
        },

        products: {
          total:
            (usageData.productsData && usageData.productsData.totalProducts) ||
            0,
          imported:
            (usageData.productsData &&
              usageData.productsData.importedProducts) ||
            0,
          manual:
            (usageData.productsData &&
              usageData.productsData.manuallyAddedProducts) ||
            0
        },
        documents: {
          total: 120,
          inContacts: 30,
          inApplications: 20,
          inClientPortal: 60,
          fromTask: 10
        },
        notes: {
          total: (usageData.notesData && usageData.notesData.totalNotes) || 0,
          inContacts:
            (usageData.notesData &&
              usageData.notesData.notesByType &&
              usageData.notesData.notesByType.client) ||
            0,
          inApplications:
            (usageData.notesData &&
              usageData.notesData.notesByType &&
              usageData.notesData.notesByType.application_stage) ||
            0,
          inOfficeCheckin:
            (usageData.notesData &&
              usageData.notesData.notesByType &&
              usageData.notesData.notesByType["office-check-in"]) ||
            0,
          inAgents:
            (usageData.notesData &&
              usageData.notesData.notesByType &&
              usageData.notesData.notesByType.agent) ||
            0,
          inPartners:
            (usageData.notesData &&
              usageData.notesData.notesByType &&
              usageData.notesData.notesByType.partner) ||
            0
        },
        quotation: {
          template:
            parseInt(
              usageData.quotationsData &&
                usageData.quotationsData.totalQuotationTemplates
            ) || 0,
          created:
            parseInt(
              usageData.quotationsData &&
                usageData.quotationsData.totalQuotations
            ) || 0,
          sent:
            parseInt(
              usageData.quotationsData &&
                usageData.quotationsData.sentQuotations
            ) || 0
        },
        invoice: [
          { type: "Net Claim Commission Invoice", paid: 120, unpaid: 5 },
          { type: "Gross Claim Commission Invoice", paid: 98, unpaid: 7 },
          { type: "Client General Invoice", paid: 85, unpaid: 3 },
          { type: "Partner General Invoice", paid: 60, unpaid: 2 },
          { type: "Payable Income Sharing", paid: 200, unpaid: 10 },
          { type: "Receivable Income Sharing", paid: 150, unpaid: 8 },
          { type: "Group Invoice", paid: 75, unpaid: 4 }
        ],
        documentChecklist: {
          total: 200,
          active: 120,
          inactive: 80,
          activeTypes: 16
        },
        paymentSchedule: {
          created: 16
        },
        agentConnect: {
          connected:
            (usageData.agentConnectData &&
              usageData.agentConnectData.agentConnectByStatus &&
              usageData.agentConnectData.agentConnectByStatus.connected) ||
            0,
          applicationsReceived:
            (usageData.agentConnectData &&
              usageData.agentConnectData
                .agentConnectApplicationSharingByStatus &&
              usageData.agentConnectData.agentConnectByStatus
                .applicationsReceived) ||
            0,
          applicationsSent:
            (usageData.agentConnectData &&
              usageData.agentConnectData
                .agentConnectApplicationSharingByStatus &&
              usageData.agentConnectData.agentConnectByStatus.requested) ||
            0,
          applicationDisconnected:
            (usageData.agentConnectData &&
              usageData.agentConnectData.agentConnectByStatus &&
              usageData.agentConnectData.agentConnectByStatus.disconnected) ||
            0,
          requestSent:
            (usageData.agentConnectData &&
              usageData.agentConnectData.agentConnectByStatus &&
              usageData.agentConnectData.agentConnectByStatus.request_sent) ||
            0,
          agentDisconnected:
            (usageData.agentConnectData &&
              usageData.agentConnectData.agentConnectByStatus &&
              usageData.agentConnectData.agentConnectByStatus.disconnected) ||
            0,
          documentsExchanged:
            (usageData.agentConnectData &&
              usageData.agentConnectData
                .agentConnectApplicationDocumentExchanged &&
              usageData.agentConnectData
                .agentConnectApplicationDocumentExchanged
                .totalDocumentsExchanged) ||
            0,
          comments:
            (usageData.agentConnectData &&
              usageData.agentConnectData.comments) ||
            0
        },
        smsUsage: {
          sent:
            parseInt(usageData.smsUsages && usageData.smsUsages.totalSmsSent) ||
            0,
          received:
            parseInt(
              usageData.smsUsages && usageData.smsUsages.totalSmsReceived
            ) || 0,
          totalMessages:
            parseInt(
              usageData.smsUsages && usageData.smsUsages.totalSmsUsages
            ) || 0,
          failed:
            parseInt(
              usageData.smsUsages && usageData.smsUsages.totalSmsFailed
            ) || 0,
          delivered:
            parseInt(
              usageData.smsUsages && usageData.smsUsages.totalSmsDelivered
            ) || 0,
          avgPerDay:
            parseInt(
              usageData.smsUsages && usageData.smsUsages.averageSmsPerDay
            ) || 0,
          successRate:
            parseInt(
              usageData.smsUsages && usageData.smsUsages.totalSmsSuccessRate
            ) || 0
        },
        whatsappUsage: {
          sent:
            parseInt(
              usageData.whatsappUsages &&
                usageData.whatsappUsages.totalWhatsappSent
            ) || 0,
          received:
            parseInt(
              usageData.whatsappUsages &&
                usageData.whatsappUsages.totalWhatsappReceived
            ) || 0,
          totalChats:
            parseInt(
              usageData.whatsappUsages &&
                usageData.whatsappUsages.totalWhatsappUsages
            ) || 0,
          failed:
            parseInt(
              usageData.whatsappUsages &&
                usageData.whatsappUsages.totalWhatsappFailed
            ) || 0,
          delivered:
            parseInt(
              usageData.whatsappUsages &&
                usageData.whatsappUsages.totalWhatsappDelivered
            ) || 0,
          activeConversations:
            parseInt(
              usageData.whatsappUsages &&
                usageData.whatsappUsages.activeConversations
            ) || 0,
          successRate:
            (usageData.whatsappUsages &&
              usageData.whatsappUsages.totalWhatsappSuccessRate) ||
            0
        },
        emailUsage: {
          sent:
            parseInt(
              usageData.emailUsagesData && usageData.emailUsagesData.sent
            ) || 0,
          received:
            parseInt(
              usageData.emailUsagesData && usageData.emailUsagesData.received
            ) || 0,
          totalEmails:
            parseInt(
              usageData.emailUsagesData && usageData.emailUsagesData.totalEmails
            ) || 0,
          failed:
            parseInt(
              usageData.emailUsagesData && usageData.emailUsagesData.failed
            ) || 0,
          delivered:
            parseInt(
              usageData.emailUsagesData && usageData.emailUsagesData.delivered
            ) || 0,
          bounceRate:
            parseInt(
              usageData.emailUsagesData && usageData.emailUsagesData.bounceRate
            ) || 0,
          successRate:
            parseInt(
              usageData.emailUsagesData && usageData.emailUsagesData.successRate
            ) || 0
        },
        emailDelivery: {
          sent: 3450,
          delivered: 3200,
          opened: 2150,
          clicked: 890
        },
        whatsappDelivery: {
          sent: 2340,
          delivered: 2200,
          read: 1890
        },
        activations: {
          clientPortal:
            parseInt(
              usageData.contactsUsageData &&
                usageData.contactsUsageData.clientPortalActivated
            ) || 0,
          companyEmail: 0
        },
        dataImport: [
          {
            category: "Contacts(Old)",
            total:
              (usageData.dataImported &&
                usageData.dataImported.contactsDataImported &&
                usageData.dataImported.contactsDataImported.total) ||
              0,
            completed:
              (usageData.dataImported &&
                usageData.dataImported.contactsDataImported &&
                usageData.dataImported.contactsDataImported.completed) ||
              0,
            inProgress:
              (usageData.dataImported &&
                usageData.dataImported.contactsDataImported &&
                usageData.dataImported.contactsDataImported.in_progress) ||
              0,
            queued:
              (usageData.dataImported &&
                usageData.dataImported.contactsDataImported &&
                usageData.dataImported.contactsDataImported.queue) ||
              0,
            processing:
              (usageData.dataImported &&
                usageData.dataImported.contactsDataImported &&
                usageData.dataImported.contactsDataImported.processing) ||
              0,
            failed:
              (usageData.dataImported &&
                usageData.dataImported.contactsDataImported &&
                usageData.dataImported.contactsDataImported.failed) ||
              0
          },
          {
            category: "Applications",
            total:
              (usageData.dataImported &&
                usageData.dataImported.applicationsDataImported &&
                usageData.dataImported.applicationsDataImported.total) ||
              0,
            completed:
              (usageData.dataImported &&
                usageData.dataImported.applicationsDataImported &&
                usageData.dataImported.applicationsDataImported.completed) ||
              0,
            inProgress:
              (usageData.dataImported &&
                usageData.dataImported.applicationsDataImported &&
                usageData.dataImported.applicationsDataImported.in_progress) ||
              0,
            queued:
              (usageData.dataImported &&
                usageData.dataImported.applicationsDataImported &&
                usageData.dataImported.applicationsDataImported.queue) ||
              0,
            processing:
              (usageData.dataImported &&
                usageData.dataImported.applicationsDataImported &&
                usageData.dataImported.applicationsDataImported.processing) ||
              0,
            failed:
              (usageData.dataImported &&
                usageData.dataImported.applicationsDataImported &&
                usageData.dataImported.applicationsDataImported.failed) ||
              0
          },
          {
            category: "Partner and Product",
            total:
              (usageData.dataImported &&
                usageData.dataImported.partnersDataImported &&
                usageData.dataImported.partnersDataImported.total) ||
              0,
            completed:
              (usageData.dataImported &&
                usageData.dataImported.partnersDataImported &&
                usageData.dataImported.partnersDataImported.completed) ||
              0,
            inProgress:
              (usageData.dataImported &&
                usageData.dataImported.partnersDataImported &&
                usageData.dataImported.partnersDataImported.in_progress) ||
              0,
            queued:
              (usageData.dataImported &&
                usageData.dataImported.partnersDataImported &&
                usageData.dataImported.partnersDataImported.queue) ||
              0,
            processing:
              (usageData.dataImported &&
                usageData.dataImported.partnersDataImported &&
                usageData.dataImported.partnersDataImported.processing) ||
              0,
            failed:
              (usageData.dataImported &&
                usageData.dataImported.partnersDataImported &&
                usageData.dataImported.partnersDataImported.failed) ||
              0
          }
        ],
        reports: [
          { type: "Contact Reports", success: 145, failed: 12 },
          { type: "Application Reports", success: 89, failed: 8 },
          { type: "Financial Reports", success: 67, failed: 3 }
        ],
        classroom: {
          totalSessions:
            (usageData.classroomData &&
              usageData.classroomData.totalSessions) ||
            0,
          totalStudents:
            (usageData.classroomData &&
              usageData.classroomData.totalStudents) ||
            0,
          notStarted:
            (usageData.classroomData &&
              usageData.classroomData.totalNotStartedSessions) ||
            0,
          inProgress:
            (usageData.classroomData &&
              usageData.classroomData.totalInprogressSessions) ||
            0,
          completed:
            (usageData.classroomData &&
              usageData.classroomData.totalCompletedSessions) ||
            0,
          archived:
            (usageData.classroomData &&
              usageData.classroomData.totalArchivedSessions) ||
            0
        },
        events: {
          total: 22,
          published: 15,
          draft: 5,
          archived: 2,
          contactsAdded: 200
        }
      };
    },
    goBack() {
      this.$router.push({ name: "tenant.list" });
    },

    handleFilterChange(filter) {
      // Disable user and branch filters for now
      if (filter.type === "user" || filter.type === "branch") {
        return;
      }

      this.filters[filter.type] = filter.value;
    },

    async handleDateFilterApplied(dateRange) {
      this.dateFilters.from_date = dateRange.from;
      this.dateFilters.to_date = dateRange.to;

      // Refetch usage data with date parameters
      await this.fetchUsageData();
    },

    async handleDateFilterCleared() {
      this.dateFilters.from_date = null;
      this.dateFilters.to_date = null;

      // Refetch usage data without date parameters
      await this.fetchUsageData();
    }
  }
};
</script>

<style scoped>
/* Component specific styles if needed */
</style>
